/**
 * Client-side tool registrations (Design note I — the three flavors).
 *
 * Side-effect module: imported once by the chat hook so the registry is
 * populated before the AG-UI stream is consumed. Registering here ONLY
 * sets render/handler/transport — it NEVER advertises a tool to the
 * model (the worker tool files + the skill-gate stay the sole authority;
 * see registerAgentTool.ts HARD RULE).
 *
 * | tool                         | flavor          | transport         |
 * |------------------------------|-----------------|-------------------|
 * | todowrite                    | render-only     | 'none'            |
 * | papan_question/_permission   | interactive O1  | 'out-of-band'     |
 * | db_editor                    | client-executed | 'client-executed' |
 */
import { registerAgentTool, type AgentToolResult } from './registerAgentTool.js'
import { workspaceScopeRoots, workspaceScopeLoaded, workspaceIdOf, isShownWithin, workspaceFileLink, isRenderedOnScreen } from '@papan/core-ui/ui/surfaceScope'
import { CLIENT_TOOL_TARGET_ARG, readClientToolTarget, type ClientToolTarget } from '../core/client-tool-target.js'
import { pickDbEditor, isDatabaseFileName, type DbEditorEntry } from './dbEditorTarget.js'
// Side-effect: register the access/sharing READ frontend tools. Live today:
// `share_link` and `manage_access`. `team` left for the worker in #386, and
// `chain_folder` in #518A — its entry survives only so a PREVIOUS-BUILD worker's
// dispatch still resolves. Same client-executed flavor as db_editor below — see
// registerAccessTools.ts. MUTATIONS are gated by
// AccessApprovalCard (MessageList), not registered here.
import './registerAccessTools.js'
// Side-effect: register the preview_screenshot client handler (captures an
// app preview via the export rasterizer and returns the image over the
// AG-UI tool-result channel).
import './previewScreenshotClient.js'
// Side-effect: register the preview_eval client handler (evaluates a JS snippet
// in the live preview iframe's bridge and returns the bounded result — the
// "ask the running app" leg of the app-inspect triad).
import './previewEvalClient.js'

// ── render-only (step 2). Proves the simplest flavor with ZERO
// behaviour change: todowrite still executes server-side (OpenCode
// mirror, Invariant 1) and renders via the existing ToolCallBlock +
// the REST-driven TodoPanel. The entry just declares the flavor — no
// handler, no suppression, default renderer. ──
registerAgentTool('todowrite', { transport: 'none' })

// ── interactive, out-of-band (step 3). papan_question / papan_permission
// are bridge-wrapped HITL transport: suppressed as normal tool-call
// blocks (their own QuestionPrompt/PermissionPrompt render them) and
// resolved via the EXISTING unchanged /api/agent/{questions,permissions}
// /:id/reply endpoints. O1 invariant — cascade-reject + always-allow
// stay in the core; we do NOT move these to the AG-UI tool-result
// channel. No handler here: the proven replyQuestion/replyPermission
// path is untouched; the registry only owns the suppression decision
// (collapsing the hand-wired name lists in agui-to-stream). ──
registerAgentTool('papan_question', { transport: 'out-of-band' })
registerAgentTool('papan_permission', { transport: 'out-of-band' })

// ── client-executed (step 4). db_editor is a REAL model tool the
// BROWSER executes. It streams as a normal tool-call block; the runtime
// (hooks.ts) runs this handler and re-POSTs an AG-UI tool-result, which
// the ingress routes to the worker's blocked ctx.clientTool. The worker
// stays the SOLE advertiser + skill-gate authority (requiredSkills:
// ['db-editor-manual'] in tools/db-editor.ts is untouched) — this entry
// only moves the *result channel*, never the declaration or gate.
//
// Cross-feature boundary: the actual apply lives in DatabaseViewer
// (features/collab). We stay decoupled via the existing window-event
// idiom (papan:db-set-*, papan:editor-action-preview, …): dispatch a
// request, await a matching result. DatabaseViewer applies the action
// and replies WITHOUT the bespoke /editor-actions/:id/reply POST — the
// runtime owns delivery now (AG-UI tool-result). Which editor answers is
// decided BEFORE dispatch (pickDbEditor — #352), so "none open" is an
// immediate error; the 20 s client-side timeout only bounds an editor that
// was picked but does not answer.
/** Every open database editor — EVERY mounted DatabaseViewer, each with its
 *  workspace, file and active flag (the window-registry idiom, decoupled
 *  cross-feature). Node/SSR-safe: no window ⇒ none. Shared by the `ready()`
 *  gate and the handler so the two can never disagree about which editor a
 *  call drives. */
function dbEditorEntries(): DbEditorEntry[] {
  if (typeof window === 'undefined') return []
  type Raw = { fileId: string; fileName: string; active: boolean; el?: Element | null; workspaceId?: string | null }
  const reg = (window as unknown as { __papanDbEditors?: Map<string, Raw> }).__papanDbEditors
  // The workspace is read NOW, from the editor's place in the page: a stack
  // can be re-marked without the editor remounting (chat-home follows the
  // conversation picked), so a value captured at registration goes stale.
  return reg
    ? [...reg.entries()].map(([key, e]) => ({
        key, fileId: e.fileId, fileName: e.fileName, active: e.active,
        onScreen: !!e.el && isRenderedOnScreen(e.el),
        workspaceId: e.el !== undefined ? workspaceIdOf(e.el) : (e.workspaceId ?? null),
      }))
    : []
}

/** A deep link that opens the file IN the call's workspace — not on the page
 *  currently on screen, which for a call recovered in a hidden workspace was
 *  another workspace (and the bound call could never succeed from there). */
const dbEditorLinkFor = (target: ClientToolTarget | null) =>
  (fileId: string) => workspaceFileLink(target?.workspaceId ?? null, fileId)

/** Is the call's own editor still MOUNTING — so the right answer is "wait",
 *  not "not open"? A reload restores a workspace's config first and its tabs
 *  a round trip later, and each tab's lazy editor chunk after that:
 *   • a stack of that workspace whose tabs are not restored yet;
 *   • or a database tab there that has not registered its editor yet — the
 *     chip's file when the chip named a database, else the SHOWN tab (the
 *     same rule `pickDbEditor` falls back to). */
// A workspace with NO marked stack (never opened, or its config not loaded,
// so it has no id yet) has no editor to wait for: the call answers at once
// with an accurate error. A stack that IS marked but went hidden before its
// tabs were restored stays unloaded until shown again (useWorkspace loads an
// inactive deck lazily), so a call bound to it waits for the user to return —
// up to the worker's own ctx.clientTool wait: the bound editor does not exist
// yet, and any other one is the wrong one.
function dbEditorStillMounting(target: ClientToolTarget, entries: DbEditorEntry[]): boolean {
  const roots = workspaceScopeRoots(target.workspaceId)
  if (roots.length === 0) return false
  if (roots.some(r => !workspaceScopeLoaded(r))) return true
  const registered = new Set(entries.filter(e => e.workspaceId === target.workspaceId).map(e => e.fileId))
  const chipDb = !!target.fileId && isDatabaseFileName(target.fileName)
  return roots.some(root => Array.from(root.querySelectorAll(
    '[data-testid="tab-viewer"][data-viewer-family="sqlite"], [data-testid="tab-viewer"][data-viewer-family="firebird"]',
  )).some(tab => {
    const id = tab.getAttribute('data-file-id')
    if (!id || registered.has(id)) return false
    if (chipDb) return id === target.fileId
    return isShownWithin(tab, root)
  }))
}

registerAgentTool('db_editor', {
  transport: 'client-executed',
  // The worker stamps the turn's target (`__target`, #352) on the args it
  // dispatches — so the handler reads THOSE, never the model's raw call.
  argsFrom: 'worker',
  // Readiness gate — consulted on any dispatch a RETRY will follow (the
  // `/pending` re-run and the worker's live `client-tool-request`, both
  // already recorded by the worker). Not ready = the call's OWN editor is a
  // tab that is still mounting (a reload restores tabs before their lazy
  // editors): the runtime neither consumes the call nor POSTs, and the next
  // 5 s poll retries — bounded by the worker's own ctx.clientTool wait.
  // Anything else answers at once (an editor, or an accurate error), so a
  // file that simply is not open never hangs the run.
  ready: (args) => {
    const target = readClientToolTarget(args)
    // No target (an older worker): answer at once — the handler's own error
    // is accurate, and waiting would hang the run for the worker's 5 min.
    if (!target) return true
    const entries = dbEditorEntries()
    if (pickDbEditor(entries, target, dbEditorLinkFor(target)).kind === 'editor') return true
    return !dbEditorStillMounting(target, entries)
  },
  // #352 owner ruling 2026-10-19: an editor action only sets UI state — unless it
  // auto-runs, which EXECUTES the SQL (it can write), so it never replays. Decided on
  // autoRun for EVERY action, by the viewer's own truthiness: DatabaseViewer runs a
  // navigate's SQL too (`pushAiSql(sql, !!payload.autoRun)`, mode sql).
  replayable: (args) => !args.autoRun,
  // #352 R-22: a replay restores the database the call actually DROVE — pin the
  // pick's file into the target, so pickDbEditor binds the replay to that file only
  // (not open → canResolve false → the replay waits, never another tab).
  pinReplayTarget: (args) => {
    const target = readClientToolTarget(args)
    if (!target) return undefined
    const pick = pickDbEditor(dbEditorEntries(), target, dbEditorLinkFor(target))
    if (pick.kind !== 'editor') return undefined
    return { ...args, [CLIENT_TOOL_TARGET_ARG]: { ...target, fileId: pick.entry.fileId, fileName: pick.entry.fileName } }
  },
  // #352 I1: this tab holds the call's own editor — the handler's exact pick.
  canResolve: (args) => pickDbEditor(dbEditorEntries(), readClientToolTarget(args), dbEditorLinkFor(readClientToolTarget(args))).kind === 'editor',
  async handler(args): Promise<AgentToolResult> {
    if (typeof window === 'undefined') {
      return { success: false, error: 'No browser context for db_editor' }
    }
    // Exactly ONE editor answers: the one this call is bound to (see
    // dbEditorTarget.ts) — never "whichever is focused", which on the
    // recovery path was another session's database.
    const target = readClientToolTarget(args)
    const pick = pickDbEditor(dbEditorEntries(), target, dbEditorLinkFor(target))
    if (pick.kind === 'error') return { success: false, error: pick.error }
    const { [CLIENT_TOOL_TARGET_ARG]: _target, ...actionArgs } = args
    const requestId = `ct_${Date.now()}_${Math.random().toString(36).slice(2)}`
    return await new Promise<AgentToolResult>((resolve) => {
      const onResult = (e: Event) => {
        const d = (e as CustomEvent).detail
        if (!d || d.requestId !== requestId) return
        cleanup()
        resolve((d.result as AgentToolResult) ?? { success: false, error: 'Empty editor result' })
      }
      const cleanup = () => {
        clearTimeout(timer)
        window.removeEventListener('papan:client-tool-result', onResult)
      }
      const timer = setTimeout(() => {
        cleanup()
        resolve({ success: false, error: 'Editor action timed out — the database editor did not answer' })
      }, 20_000)
      window.addEventListener('papan:client-tool-result', onResult)
      window.dispatchEvent(
        new CustomEvent('papan:client-tool-request', {
          // `editor` names the one viewer that may answer (DatabaseViewer).
          detail: { requestId, toolName: 'db_editor', args: actionArgs, editor: { key: pick.entry.key } },
        }),
      )
    })
  },
})
